import { eachDayOfInterval, format, subDays } from 'date-fns'
import { BarChart3 } from 'lucide-react'
import { useMemo } from 'react'
import { Bar, BarChart, CartesianGrid } from 'recharts'
import { type ChartConfig, ChartContainer } from '@/components/sv/chart'
import { H3 } from '@/components/sv/typographies'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'

const getRandomNumber = () => Math.floor(Math.random() * 10) + 1

const Divider = () => <div className="w-px bg-border" />

const SummaryItemSkeleton = () => (
  <div className="flex-1 min-w-0 px-6 py-4">
    <Skeleton className="h-3 w-20 mb-2" />
    <Skeleton className="h-7 w-24" />
  </div>
)

export const SpendingOverviewFallback = () => {
  const chartData = useMemo(() => {
    const now = new Date()
    const interval = eachDayOfInterval({
      start: subDays(now, 31),
      end: now,
    })

    return interval.map((date) => ({
      day: format(date, 'MMM dd'),
      value: getRandomNumber(),
    }))
  }, [])

  const chartConfig = useMemo(() => {
    return {
      value: {
        label: 'Spending',
        color: 'var(--accent)',
      },
    }
  }, []) satisfies ChartConfig

  return (
    <Card className="bg-secondary p-0 gap-0">
      <CardHeader className="px-5 py-3 flex flex-row items-center justify-between gap-0">
        <CardTitle>
          <H3 className="flex items-center gap-2 text-sm text-muted-foreground">
            <BarChart3 className="size-4" /> Spending overview
          </H3>
        </CardTitle>
        <div className="flex items-center gap-4">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-5 w-24" />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Card className="p-0 border-x-0 border-b-0 shadow-none">
          <CardContent className="p-0">
            <div className="flex">
              <SummaryItemSkeleton />
              <Divider />
              <SummaryItemSkeleton />
              <Divider />
              <SummaryItemSkeleton />
              <Divider />
              <SummaryItemSkeleton />
            </div>
            <Separator />
            <div className="py-6 px-6">
              <ChartContainer
                config={chartConfig}
                className="min-h-[250px] max-h-[350px] w-full [&_.recharts-responsive-container]:max-h-[350px]"
              >
                <BarChart accessibilityLayer data={chartData}>
                  <CartesianGrid vertical={false} className="opacity-20" />
                  <Bar
                    dataKey="value"
                    fill="var(--accent)"
                    className="animate-pulse"
                  />
                </BarChart>
              </ChartContainer>
            </div>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  )
}
